import { useEffect, useMemo, useRef, useState } from "react";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import listPlugin from "@fullcalendar/list";
import interactionPlugin from "@fullcalendar/interaction";
import type {
  DatesSetArg,
  EventClickArg,
  EventContentArg,
  EventInput,
} from "@fullcalendar/core";
import type { DateClickArg } from "@fullcalendar/interaction";
import {
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  Loader2,
  Plus,
} from "lucide-react";
import { AppLayout } from "@/components/layout/AppLayout";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useCalendarEvents } from "../hooks/useCalendar";
import { CalendarFilters } from "../components/CalendarFilters";
import { MiniCalendar } from "../components/MiniCalendar";
import { EventPopover } from "../components/EventPopover";
import { NewMeetingDrawer } from "@/modules/meetings/components/NewMeetingDrawer";
import type { CalendarEvent, CalendarEventType } from "../types";
import "../calendar.css";

type ViewKey = "dayGridMonth" | "timeGridWeek" | "timeGridDay" | "listMonth";

const VIEW_TABS: { key: ViewKey; label: string }[] = [
  { key: "dayGridMonth", label: "Month" },
  { key: "timeGridWeek", label: "Week" },
  { key: "timeGridDay", label: "Day" },
  { key: "listMonth", label: "Agenda" },
];

const ALL_TYPES: CalendarEventType[] = ["meeting", "task", "action", "milestone"];
const VIEW_STORAGE_KEY = "phazex_calendar_view";

function readStoredView(): ViewKey {
  if (typeof window === "undefined") return "dayGridMonth";
  try {
    const v = window.localStorage.getItem(VIEW_STORAGE_KEY);
    if (v && VIEW_TABS.some((t) => t.key === v)) return v as ViewKey;
  } catch {
    // ignore
  }
  return "dayGridMonth";
}

function writeStoredView(v: ViewKey) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(VIEW_STORAGE_KEY, v);
  } catch {
    // ignore
  }
}

function getRangeForView(view: ViewKey, anchor: Date): {
  fetchFrom: Date;
  fetchTo: Date;
} {
  const oneWeek = 7 * 24 * 60 * 60 * 1000;
  if (view === "dayGridMonth" || view === "listMonth") {
    const first = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
    const last = new Date(anchor.getFullYear(), anchor.getMonth() + 1, 0);
    return {
      fetchFrom: new Date(first.getTime() - oneWeek),
      fetchTo: new Date(last.getTime() + oneWeek),
    };
  }
  if (view === "timeGridWeek") {
    // Monday-first week
    const dow = (anchor.getDay() + 6) % 7;
    const monday = new Date(anchor);
    monday.setDate(anchor.getDate() - dow);
    monday.setHours(0, 0, 0, 0);
    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);
    sunday.setHours(23, 59, 59, 999);
    return {
      fetchFrom: new Date(monday.getTime() - oneWeek),
      fetchTo: new Date(sunday.getTime() + oneWeek),
    };
  }
  // timeGridDay
  const start = new Date(anchor);
  start.setHours(0, 0, 0, 0);
  const end = new Date(anchor);
  end.setHours(23, 59, 59, 999);
  return {
    fetchFrom: new Date(start.getTime() - oneWeek),
    fetchTo: new Date(end.getTime() + oneWeek),
  };
}

function formatPeriodTitle(view: ViewKey, anchor: Date): string {
  if (view === "dayGridMonth" || view === "listMonth") {
    return anchor.toLocaleDateString("en-GB", {
      month: "long",
      year: "numeric",
    });
  }
  if (view === "timeGridWeek") {
    const dow = (anchor.getDay() + 6) % 7;
    const monday = new Date(anchor);
    monday.setDate(anchor.getDate() - dow);
    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);
    const monthSame = monday.getMonth() === sunday.getMonth();
    const start = monday.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: monthSame ? "short" : "short",
    });
    const end = sunday.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
    return `${start} – ${end}`;
  }
  return anchor.toLocaleDateString("en-GB", {
    weekday: "long",
    day: "2-digit",
    month: "long",
    year: "numeric",
  });
}

function typeIcon(type: CalendarEventType): string {
  if (type === "meeting") return "📅";
  if (type === "task") return "✓";
  if (type === "action") return "⚡";
  return "◆";
}

export default function CalendarPage() {
  const calendarRef = useRef<FullCalendar | null>(null);
  const [currentView, setCurrentView] = useState<ViewKey>(readStoredView());
  const [anchorDate, setAnchorDate] = useState<Date>(new Date());
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());

  const [enabledTypes, setEnabledTypes] = useState<Set<CalendarEventType>>(
    () => new Set(ALL_TYPES),
  );
  const [assignedToMe, setAssignedToMe] = useState(false);
  const [companyId, setCompanyId] = useState<string | null>(null);
  const [projectId, setProjectId] = useState<string | null>(null);

  const [popover, setPopover] = useState<{
    event: CalendarEvent;
    position: { top: number; left: number };
  } | null>(null);

  const [meetingDrawerOpen, setMeetingDrawerOpen] = useState(false);

  const range = useMemo(
    () => getRangeForView(currentView, anchorDate),
    [currentView, anchorDate],
  );

  const eventsQuery = useCalendarEvents({
    dateFrom: range.fetchFrom.toISOString(),
    dateTo: range.fetchTo.toISOString(),
    types: Array.from(enabledTypes),
    companyId: companyId ?? undefined,
    projectId: projectId ?? undefined,
    assignedToMe,
  });

  const events: CalendarEvent[] = eventsQuery.data ?? [];

  const fcEvents: EventInput[] = useMemo(
    () =>
      events.map((ev) => ({
        id: `${ev.type}-${ev.id}`,
        title: ev.title,
        start: ev.start,
        end: ev.end,
        allDay: ev.allDay,
        backgroundColor: ev.color,
        borderColor: ev.color,
        textColor: ev.color,
        extendedProps: { calendarEvent: ev },
      })),
    [events],
  );

  function changeView(v: ViewKey) {
    setCurrentView(v);
    writeStoredView(v);
    calendarRef.current?.getApi().changeView(v);
  }

  function goToday() {
    calendarRef.current?.getApi().today();
    const today = new Date();
    setAnchorDate(today);
    setSelectedDate(today);
  }

  function goPrev() {
    calendarRef.current?.getApi().prev();
  }

  function goNext() {
    calendarRef.current?.getApi().next();
  }

  function goToDate(d: Date) {
    calendarRef.current?.getApi().gotoDate(d);
    setSelectedDate(d);
    setAnchorDate(d);
  }

  function handleDatesSet(arg: DatesSetArg) {
    const center = new Date(
      (arg.start.getTime() + arg.end.getTime()) / 2,
    );
    setAnchorDate(center);
  }

  function handleEventClick(arg: EventClickArg) {
    arg.jsEvent.preventDefault();
    const ce = (arg.event.extendedProps as { calendarEvent?: CalendarEvent })
      .calendarEvent;
    if (!ce) return;
    const rect = (arg.el as HTMLElement).getBoundingClientRect();
    setPopover({
      event: ce,
      position: { top: rect.top, left: rect.right + 8 },
    });
  }

  function handleDateClick(_arg: DateClickArg) {
    // Reserved for quick-create on empty slots in week/day view.
    // Future enhancement: open NewMeetingDrawer with date pre-filled.
  }

  function renderEventContent(arg: EventContentArg) {
    const ce = (arg.event.extendedProps as { calendarEvent?: CalendarEvent })
      .calendarEvent;
    const color = ce?.color ?? "#3B82F6";
    const isMonth = arg.view.type === "dayGridMonth";
    const isList = arg.view.type.startsWith("list");

    if (isList) {
      return (
        <div className="flex items-center gap-2 py-1">
          <span
            className="inline-block h-3 w-1 shrink-0 rounded-sm"
            style={{ background: color }}
          />
          <span className="text-[12px]">{typeIcon(ce?.type ?? "meeting")}</span>
          <span className="truncate text-[13px] font-medium text-[hsl(215,16%,20%)]">
            {arg.event.title}
          </span>
          {ce?.projectName && (
            <span className="truncate text-[11px] text-muted-foreground">
              · {ce.projectName}
            </span>
          )}
        </div>
      );
    }

    return (
      <div
        className="flex items-center gap-1 truncate rounded-sm px-1 py-0.5"
        style={{
          background: color + "1F",
          borderLeft: `3px solid ${color}`,
          color,
          fontSize: 11,
          fontWeight: 500,
          minWidth: 0,
        }}
      >
        {isMonth && !arg.event.allDay && (
          <span className="text-[10px] tabular-nums opacity-90">
            {arg.timeText}
          </span>
        )}
        <span className="truncate">
          {typeIcon(ce?.type ?? "meeting")} {arg.event.title}
        </span>
      </div>
    );
  }

  const breadcrumb = [
    { label: "PhazeX Hub", href: "/" },
    { label: "Calendar" },
  ];

  function toggleType(t: CalendarEventType) {
    setEnabledTypes((prev) => {
      const next = new Set(prev);
      if (next.has(t)) next.delete(t);
      else next.add(t);
      return next;
    });
  }

  // Sync FC view to stored view on initial mount in case ref initialized late.
  useEffect(() => {
    const api = calendarRef.current?.getApi();
    if (api && api.view.type !== currentView) {
      api.changeView(currentView);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <AppLayout breadcrumb={breadcrumb}>
      <div className="mb-4 flex items-center gap-2">
        <CalendarDays className="h-5 w-5 text-[hsl(210,68%,55%)]" strokeWidth={1.75} />
        <h1 className="text-[20px] font-semibold text-foreground">Calendar</h1>
        {eventsQuery.isFetching && (
          <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
        )}
        <div className="ml-auto">
          <Button size="sm" onClick={() => setMeetingDrawerOpen(true)}>
            <Plus className="mr-1 h-3.5 w-3.5" strokeWidth={1.5} />
            New Meeting
          </Button>
        </div>
      </div>

      <div className="flex gap-4">
        {/* LEFT SIDEBAR */}
        <aside className="w-[260px] shrink-0 space-y-4">
          <div
            className="rounded-xl border bg-white p-4"
            style={{ borderColor: "hsl(210,30%,88%)" }}
          >
            <Button
              variant="outline"
              size="sm"
              className="mb-3 h-8 w-full text-[12px]"
              onClick={goToday}
            >
              Today
            </Button>

            <MiniCalendar
              selectedDate={selectedDate}
              onDateSelect={(d) => goToDate(d)}
              onMonthChange={(d) => goToDate(d)}
              events={events}
            />
          </div>

          <div
            className="rounded-xl border bg-white p-4"
            style={{ borderColor: "hsl(210,30%,88%)" }}
          >
            <CalendarFilters
              enabledTypes={enabledTypes}
              onToggleType={toggleType}
              assignedToMe={assignedToMe}
              onAssignedToMeChange={setAssignedToMe}
              companyId={companyId}
              onCompanyChange={setCompanyId}
              projectId={projectId}
              onProjectChange={setProjectId}
              events={events}
            />
          </div>
        </aside>

        {/* MAIN CALENDAR */}
        <div className="min-w-0 flex-1">
          <div
            className="mb-3 flex items-center justify-between rounded-xl border bg-white px-3 py-2"
            style={{ borderColor: "hsl(210,30%,88%)" }}
          >
            <div className="flex items-center gap-1">
              {VIEW_TABS.map((t) => (
                <button
                  key={t.key}
                  type="button"
                  onClick={() => changeView(t.key)}
                  className={cn(
                    "rounded-md px-3 py-1 text-[12px] font-medium transition-colors",
                    currentView === t.key
                      ? "bg-[hsl(210,68%,55%)] text-white"
                      : "text-[hsl(215,16%,40%)] hover:bg-[hsl(210,50%,96%)]",
                  )}
                >
                  {t.label}
                </button>
              ))}
            </div>

            <div className="text-[13px] font-semibold text-foreground">
              {formatPeriodTitle(currentView, anchorDate)}
            </div>

            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={goPrev}
                className="flex h-7 w-7 items-center justify-center rounded border text-muted-foreground hover:bg-[hsl(210,50%,96%)]"
                style={{ borderColor: "hsl(210,30%,85%)" }}
                aria-label="Previous"
              >
                <ChevronLeft className="h-3.5 w-3.5" strokeWidth={1.5} />
              </button>
              <button
                type="button"
                onClick={goToday}
                className="rounded border px-2 py-0.5 text-[11px] text-[hsl(215,16%,40%)] hover:bg-[hsl(210,50%,96%)]"
                style={{ borderColor: "hsl(210,30%,85%)" }}
              >
                Today
              </button>
              <button
                type="button"
                onClick={goNext}
                className="flex h-7 w-7 items-center justify-center rounded border text-muted-foreground hover:bg-[hsl(210,50%,96%)]"
                style={{ borderColor: "hsl(210,30%,85%)" }}
                aria-label="Next"
              >
                <ChevronRight className="h-3.5 w-3.5" strokeWidth={1.5} />
              </button>
            </div>
          </div>

          <div
            className="phazex-calendar rounded-xl border bg-white p-3"
            style={{ borderColor: "hsl(210,30%,88%)" }}
          >
            <FullCalendar
              ref={calendarRef as never}
              plugins={[
                dayGridPlugin,
                timeGridPlugin,
                listPlugin,
                interactionPlugin,
              ]}
              initialView={currentView}
              headerToolbar={false}
              events={fcEvents}
              eventClick={handleEventClick}
              dateClick={handleDateClick}
              datesSet={handleDatesSet}
              eventContent={renderEventContent}
              height="auto"
              firstDay={1}
              nowIndicator={true}
              selectable={true}
              selectMirror={true}
              dayMaxEvents={3}
              moreLinkClick="popover"
              displayEventTime={true}
              eventTimeFormat={{
                hour: "2-digit",
                minute: "2-digit",
                hour12: false,
              }}
              slotLabelFormat={{
                hour: "2-digit",
                minute: "2-digit",
                hour12: false,
              }}
              allDaySlot={true}
              expandRows={true}
              stickyHeaderDates={true}
              listDayFormat={{
                weekday: "long",
                day: "2-digit",
                month: "long",
                year: "numeric",
              }}
            />
          </div>
        </div>
      </div>

      {popover && (
        <EventPopover
          event={popover.event}
          position={popover.position}
          onClose={() => setPopover(null)}
        />
      )}

      <NewMeetingDrawer
        isOpen={meetingDrawerOpen}
        onClose={() => setMeetingDrawerOpen(false)}
        onCreated={() => eventsQuery.refetch()}
      />
    </AppLayout>
  );
}
